import { Modal, PackageCardInput } from '@insify/design-system/components';
import { DataLeak, ElectricGoods, Heart, Legal } from '@insify/design-system/line-icons';
import { textSm } from '@insify/design-system/typography';
import { ContactOptions, RichContent } from '@insify/funnel-components';
import { formatMoney } from '@insify/global-money';
import clsx from 'clsx';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useContactOptions } from '../../../../hooks/useContactOptions';

import styles from './GhostCard.module.css';

import type { GhostCardSlug } from '../../../../models/queries';

type Props = {
  className?: string;
  icon: React.ReactNode;
  heading: string;
  description: React.ReactNode;
  details: React.ReactNode;
  priceInCents: string;
  trackingId: string;
  slug: GhostCardSlug;
};

const DeltaPrice = memo(({ cents }: { cents: string }) => {
  const { t } = useTranslation(['funnel']);
  const amount = formatMoney({ cents, currency: 'EUR' }, 'fr');

  return (
    <span className={clsx(textSm, styles.price)}>
      {t('funnel:coverage_ghost_card_price_starting_from', { amount })}
    </span>
  );
});

const GhostCard = ({ className, icon, heading, description, details, priceInCents, trackingId, slug }: Props) => {
  const { contactOptions } = useContactOptions();
  const [isChecked, setIsChecked] = useState(sessionStorage.getItem(`insify-ghost-card-${slug}`) === 'true');
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation(['funnel', 'common']);

  const handleClose = () => setIsOpen(false);
  const handleChange = () => {
    sessionStorage.setItem(`insify-ghost-card-${slug}`, JSON.stringify(!isChecked));

    if (!isChecked) {
      setIsOpen(true);
    }

    setIsChecked(!isChecked);
  };

  return (
    <div className={className}>
      <Modal open={isOpen} trackingId={trackingId} onClose={handleClose}>
        <RichContent.H2>{t('funnel:coverage_ghost_card_common_modal_heading')}</RichContent.H2>
        <RichContent.P className={styles.modalDescription}>{description}</RichContent.P>
        <RichContent.P className={styles.modalDescription}>
          {t('funnel:coverage_ghost_card_common_modal_description')}
        </RichContent.P>
        <ContactOptions contactOptions={contactOptions} className={styles.contacts} />
      </Modal>
      <PackageCardInput
        icon={icon}
        heading={heading}
        description={description}
        detailsToggleLabel={t('funnel:coverage_show_detail_button')}
        details={details}
        deltaPrice={<DeltaPrice cents={priceInCents} />}
        trackingId={trackingId}
        expandableTrackingId={trackingId}
        name={slug}
        checked={isChecked}
        onChange={handleChange}
      />
    </div>
  );
};

const slugToGhostCard: Record<
  Exclude<GhostCardSlug, 'carInsurance'>,
  ({ className }: { className?: string }) => JSX.Element
> = {
  property: ({ className }: { className?: string }) => {
    const { t } = useTranslation(['funnel']);

    return (
      <GhostCard
        className={className}
        icon={<ElectricGoods />}
        heading={t('funnel:coverage_ghost_card_property_heading')}
        description={t('funnel:coverage_ghost_card_property_description')}
        details={t('funnel:coverage_ghost_card_property_details')}
        slug={'property'}
        trackingId='property'
        priceInCents='2000'
      />
    );
  },
  legalProtection: ({ className }: { className?: string }) => {
    const { t } = useTranslation(['funnel']);

    return (
      <GhostCard
        className={className}
        icon={<Legal />}
        heading={t('funnel:coverage_ghost_card_legal_protection_heading')}
        description={t('funnel:coverage_ghost_card_legal_protection_description')}
        details={t('funnel:coverage_ghost_card_legal_protection_details')}
        slug={'legalProtection'}
        trackingId='legal-protection'
        priceInCents='2000'
      />
    );
  },
  dnoLiability: ({ className }: { className?: string }) => {
    const { t } = useTranslation(['funnel']);

    return (
      <GhostCard
        className={className}
        icon={<Heart />}
        heading={t('funnel:coverage_ghost_card_dno_liability_heading')}
        description={t('funnel:coverage_ghost_card_dno_liability_description')}
        details={t('funnel:coverage_ghost_card_dno_liability_details')}
        slug={'dnoLiability'}
        trackingId='dno-liability'
        priceInCents='2000'
      />
    );
  },
  cyber: ({ className }: { className?: string }) => {
    const { t } = useTranslation(['funnel']);

    return (
      <GhostCard
        className={className}
        icon={<DataLeak />}
        heading={t('funnel:coverage_ghost_card_cyber_heading')}
        description={t('funnel:coverage_ghost_card_cyber_description')}
        details={t('funnel:coverage_ghost_card_cyber_details')}
        slug={'cyber'}
        trackingId='cyber'
        priceInCents='2000'
      />
    );
  },
};

export { slugToGhostCard };
